import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface PriceAlert {
  id: string;
  label: string | null;
  latitude: number;
  longitude: number;
  radius_km: number;
  max_price: number;
  is_active: boolean;
  created_at: string;
}

export const usePriceAlerts = () => {
  const [alerts, setAlerts] = useState<PriceAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchAlerts = useCallback(async (uid: string) => {
    setLoading(true);
    const { data, error } = await supabase
      .from("price_alerts")
      .select("*")
      .eq("user_id", uid)
      .order("created_at", { ascending: false });
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    setAlerts((data as PriceAlert[]) ?? []);
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
        await fetchAlerts(session.user.id);
      } else setLoading(false);
    };
    init();
  }, [fetchAlerts]);

  const createAlert = async (a: Omit<PriceAlert, "id" | "created_at" | "is_active">) => {
    if (!userId) return;
    const { error } = await supabase.from("price_alerts").insert({ ...a, user_id: userId, is_active: true });
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Alert created" });
    await fetchAlerts(userId);
  };

  const toggleAlert = async (id: string, active: boolean) => {
    await supabase.from("price_alerts").update({ is_active: active }).eq("id", id);
    if (userId) await fetchAlerts(userId);
  };

  const deleteAlert = async (id: string) => {
    await supabase.from("price_alerts").delete().eq("id", id);
    if (userId) await fetchAlerts(userId);
    toast({ title: "Alert deleted" });
  };

  return { alerts, loading, createAlert, toggleAlert, deleteAlert, isAuthenticated: !!userId };
};
